import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { useAppSelector } from '@store/hooks';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { FlagOutlineIcon, IssueFilledIcon } from '@utils/icons';
import { professionChipStyle } from '@utils/professionColor';
import { StarFilledIcon } from '../../IssuesPage/Board/icons';
import { chipRoom, type Anchor } from '../MainView/buildingViewAnchors';
import { ChipRow } from '../MainView/ChipRow';
import mainStyles from '../MainView/mainView.module.css';
import { LayerChipCard } from './LayerChipCard';
import type { ILayerChip } from './layers';
import styles from './layers.module.css';

interface ILayerChipCellProps {
  anchor: Anchor;
  chips: readonly ILayerChip[];
  layer: 'activities' | 'issues';
  /** The workforce chips' shrink-to-fit factor, so both layers sit on the floor line alike. */
  scale: number;
  onSelect: (floorId: string) => void;
  /** An activity's areas → their floors, for the hover card's per-floor counts. */
  floorOfArea: ReadonlyMap<string, string>;
}

interface IChipBodyProps {
  chip: ILayerChip;
}

/** An issue chip, as Trusstor draws it: issue glyph + count, then a star when
 *  any is critical and a flag when any is past due, each behind a hairline. */
function IssueChipBody({ chip }: IChipBodyProps) {
  return (
    <>
      <span className={styles.chipSegment}>
        <IssueFilledIcon size={14} />
        {chip.count}
      </span>
      {chip.critical && (
        <span className={styles.chipSegment}>
          <StarFilledIcon size={12} />
        </span>
      )}
      {chip.late && (
        <span className={styles.chipSegment}>
          <FlagOutlineIcon size={14} />
        </span>
      )}
    </>
  );
}

/** An activity chip: count + the trade's progress here, and a flag when any is behind. */
function ActivityChipBody({ chip }: IChipBodyProps) {
  return (
    <>
      <span className={mainStyles.slabProfChipCount}>{chip.count}</span>
      <span>{chip.progress}%</span>
      {chip.late && <FlagOutlineIcon size={12} />}
    </>
  );
}

interface ILayerChipProps {
  floorId: string;
  chip: ILayerChip;
  layer: 'activities' | 'issues';
  /** An activity's areas → their floors, for the hover card's per-floor counts. */
  floorOfArea: ReadonlyMap<string, string>;
}

/** One trade's layer chip on a floor (or special area), with its hover card. */
function LayerChip({ floorId, chip, layer, floorOfArea }: ILayerChipProps) {
  const tz = useAppSelector(selectSiteTz);
  return (
    <Tooltip
      variant="marker"
      wrap
      content={<LayerChipCard floorId={floorId} chip={chip} layer={layer} floorOfArea={floorOfArea} tz={tz} />}
    >
      <span
        className={layer === 'issues' ? styles.issueChip : mainStyles.slabProfChip}
        style={professionChipStyle(chip.color)}
      >
        {layer === 'issues' ? <IssueChipBody chip={chip} /> : <ActivityChipBody chip={chip} />}
      </span>
    </Tooltip>
  );
}

interface ILayerChipRowProps extends Omit<ILayerChipProps, 'chip'> {
  chips: readonly ILayerChip[];
}

/** A special area's layer chips — every trade's; the card wraps them. */
export function LayerChipRow({ chips, ...shared }: ILayerChipRowProps) {
  return chips.map((chip) => <LayerChip key={chip.tradeId} chip={chip} {...shared} />);
}

/**
 * One floor's chips in the Activities or Issues layer on the building, at the
 * same `chip<id>` anchor as the workforce chips — as many as fit the slab, the
 * rest as "+N". A click selects the floor.
 */
export function LayerChipCell({ anchor, chips, layer, scale, onSelect, floorOfArea }: ILayerChipCellProps) {
  return (
    <div
      className={mainStyles.chipOverlayCell}
      data-testid={`layer-chips-${anchor.floorId}`}
      style={{
        left: anchor.left,
        top: anchor.top,
        width: anchor.width,
        height: anchor.height,
      }}
      onClick={() => onSelect(anchor.floorId)}
    >
      <ChipRow available={chipRoom(anchor.width, scale)} scale={scale}>
        {chips.map((chip) => (
          <LayerChip key={chip.tradeId} floorId={anchor.floorId} chip={chip} layer={layer} floorOfArea={floorOfArea} />
        ))}
      </ChipRow>
    </div>
  );
}
